import type { GrantKind } from '@app/shared/services/sadmin-grants.service';
import { i18n } from '@i18n/translations';
import type { TFunction } from 'i18next';

/**
 * Single source of truth for the Spanish display labels used across:
 *   * /account → Próximo cobro summary
 *   * /account → Available addons section
 *   * /sadmin/business-features → Cortesías tab (grant target picker)
 *   * pricing-section (fallback when API doesn't supply names)
 *
 * Keep this file as the only place that maps catalog codes →
 * user-facing Spanish strings. Adding a new module/addon/feature/quota
 * means adding ONE entry here, not three.
 */

/**
 * Module / addon / product codes (kind = 'module' | 'addon' | 'product').
 * Maps the catalog `code` to a Spanish display name.
 */
export const CATALOG_CODE_LABELS: Record<string, string> = {
  // Core
  'core.neocrm': 'Neo App Core',
  neo_crm: 'Neo App Core',
  neo_mobile: 'NeoMobile (App móvil)',
  // module.*
  'module.pos': 'Punto de Venta (POS)',
  'module.inventory_advanced': 'Inventario avanzado',
  'module.accounting_full': 'Contabilidad completa',
  // Gastos is CORE (included in every plan) — see CORE_TENANT_MODULES in
  // apps/neoapi/src/modules/business-features/business-features.constants.ts.
  // This label used to read "Finanzas (AR + AP + Gastos)", which told the
  // operator expenses were part of the paid module they were being
  // charged RD$700 for.
  'module.finance': 'Finanzas (Cuentas por Cobrar + Cuentas por Pagar)',
  'module.restaurant': 'Restaurante',
  // addon.*
  'addon.ecf': 'Facturación Electrónica ECF',
  'addon.dgii_reports': 'Reportes DGII (606 / 607 / 608)',
  'addon.bank_reconciliation': 'Conciliación bancaria',
  'addon.credit_invoices': 'Facturación a crédito',
  'addon.follow_up_reminders': 'Recordatorios de cobro',
  'addon.kds': 'Pantalla de cocina (KDS)',
  'addon.promotions': 'Promociones',
  'addon.shifts': 'Turnos y cuadre de caja',
  'addon.reports': 'Reportes operativos',
  'addon.rentals': 'Alquileres',
  'addon.support_priority': 'Soporte prioritario 24/7',
  // Quota / capacity addons (kind = 'quota'). Sold as
  // "aumentar capacidad" on /account. Codes match
  // subscription_addon_type enum + catalog_quantity_scale seeds.
  extra_user: 'Usuarios extra',
  extra_branch: 'Sucursales extra',
  extra_kds_screen: 'Pantallas KDS extra',
  extra_warehouse: 'Almacenes extra',
  extra_product: 'Productos extra',
  extra_ecf: 'e-CF extra',
  api_access: 'Acceso API',
  pos_addon: 'Terminales POS extra',
  kds_addon: 'Pantallas KDS extra',
  // Quota dimension codes — `catalog_quota.code` (migration 0217).
  // These reach the UI as subscription-item codes and as invoice line
  // `targetCode`s, so they need a label even when the catalog's own
  // `quantityScales[].name` hasn't loaded.
  users: 'Usuarios',
  branches: 'Sucursales',
  warehouses: 'Almacenes',
  products: 'Productos',
  ecf_invoices: 'e-CF por mes',
  api_requests: 'API requests/mes',
  storage_gb: 'Almacenamiento',
  documents_per_month: 'Documentos/mes',
  // Legacy module codes (predate the module.* convention; survive on
  // some grandfathered subscriptions where no new equivalent exists).
  customers: 'Clientes',
  expenses: 'Gastos',
  invoicing: 'Facturación',
  modifiers: 'Modificadores',
  purchases: 'Compras',
  reports: 'Reportes',
  shifts: 'Turnos',
};

/**
 * Capability codes (kind = 'feature'). Mirrors the CAPABILITY_CODES
 * union shipped by `@neogeniux/api-contracts`. Adding a code there
 * must also add the Spanish label here so /sadmin/business-features
 * → Cortesías can show it in the searchable picker.
 */
export const FEATURE_CODE_LABELS: Record<string, string> = {
  'inventory.basic': 'Inventario básico',
  'inventory.advanced': 'Inventario avanzado',
  'accounting.basic': 'Contabilidad básica',
  'accounting.full': 'Contabilidad completa',
  'restaurants.kds': 'Cocina (KDS)',
  'restaurants.tables': 'Restaurante / mesas',
  'restaurants.kitchen_workflows': 'Flujos de cocina',
  'branches.multi': 'Multi-sucursal',
  'ecf.enabled': 'Facturación Electrónica ECF',
  'offline.enabled': 'Modo offline',
  'multi_company.enabled': 'Multi-empresa',
  'api.access': 'Acceso API',
  'support.priority': 'Soporte prioritario',
  'invoice.free_items': 'Facturación con ítems libres',
  'invoice.credit_invoices': 'Facturación a crédito',
  'invoice.follow_up_reminders': 'Recordatorios de cobro',
  'reports.enabled': 'Reportes operativos',
  'promotions.enabled': 'Promociones',
  'shifts.enabled': 'Turnos y cuadre de caja',
};

/** Spanish labels for the GrantKind enum on subscription_override. */
export const GRANT_KIND_LABELS: Record<GrantKind, string> = {
  module: 'Módulo',
  addon: 'Addon',
  feature: 'Capacidad',
  quota: 'Cuota',
};

/**
 * Universal lookup — falls through CATALOG → FEATURE maps and finally
 * the raw code so callers always get a renderable string.
 */
export function labelForCode(code: string): string {
  return CATALOG_CODE_LABELS[code] ?? FEATURE_CODE_LABELS[code] ?? code;
}

/**
 * Turn a billing-invoice line into something a customer can read.
 *
 * The stored `description` is only sometimes human. The writer
 * resolves a catalog name when it can ("almacén adicional ×1",
 * "ITBIS 18%", "Ajuste — Descuento") and otherwise falls back to a
 * machine shape — `plan_template:business × 1`, `addon:extra_user × 8`
 * — which then printed verbatim on the invoice PDF.
 *
 * Resolution order:
 *   1. `itemKind` + `targetCode` through the catalog label map above,
 *      which is also what /account's Próximo cobro breakdown uses.
 *      Plan templates go through `planName` so the name follows the
 *      catalog rather than a second hardcoded list (see the
 *      plan-name resolution rule in apps/neocrm/CLAUDE.md).
 *   2. The same parse applied to the stored description, for historic
 *      rows whose itemKind/targetCode columns were never populated.
 *   3. The stored description untouched — already human.
 *
 * Quantity is appended as `× n` only when it is greater than one, so
 * a single plan does not read "Negocio × 1".
 */
export function resolveInvoiceLineLabel(
  line: {
    description: string;
    itemKind?: string | null;
    targetCode?: string | null;
    quantity?: string | number | null;
  },
  /** Resolves a plan-template code to its catalog name. */
  planName?: (code: string) => string | undefined
): string {
  const qty = Number(line.quantity ?? 1);
  const suffix = Number.isFinite(qty) && qty > 1 ? ` × ${qty}` : '';

  const named = (kind: string, code: string): string | undefined => {
    if (kind === 'plan_template') {
      return planName?.(code) ?? CATALOG_CODE_LABELS[code];
    }
    return CATALOG_CODE_LABELS[code] ?? FEATURE_CODE_LABELS[code];
  };

  if (line.itemKind && line.targetCode) {
    const label = named(line.itemKind, line.targetCode);
    if (label) return `${label}${suffix}`;
  }

  // Historic rows: the columns are null but the machine shape is
  // still sitting in the description.
  const parsed = /^([a-z_]+):([a-z0-9_.-]+)(?:\s*×.*)?$/i.exec(
    line.description.trim()
  );
  if (parsed) {
    const label = named(parsed[1], parsed[2]);
    if (label) return `${label}${suffix}`;
  }

  return line.description;
}

/**
 * The FE writes raw English summaries on the
 * `subscription_change_request` row (e.g. `Remove addon.shifts from
 * /account`). Parse the known patterns so the operator sees a
 * localized phrase with the catalog label instead of the SKU.
 */
export function translateChangeSummary(
  raw: string | null | undefined,
  t: TFunction
): string {
  if (!raw) return '';
  const removeMatch = raw.match(/^Remove\s+(\S+)\s+from\s+\/account$/i);
  if (removeMatch) {
    return t(i18n.ACCOUNT.SCHEDULED_SUMMARY_REMOVE, {
      name: labelForCode(removeMatch[1]),
    } as Record<string, unknown>);
  }
  const addMatch = raw.match(/^Add\s+(\S+)\s+from\s+\/account$/i);
  if (addMatch) {
    return t(i18n.ACCOUNT.SCHEDULED_SUMMARY_ADD, {
      name: labelForCode(addMatch[1]),
    } as Record<string, unknown>);
  }
  const planMatch = raw.match(/^Change\s+plan\s+to\s+(\S+)/i);
  if (planMatch) {
    return t(i18n.ACCOUNT.SCHEDULED_SUMMARY_PLAN_CHANGE, {
      name: labelForCode(planMatch[1]),
    } as Record<string, unknown>);
  }
  return raw;
}
